import { Injectable, inject } from '@angular/core';
import { Assignment } from '@core/interfaces/assignment';
import { MyReportApprovalListItem } from '@core/interfaces/my-report-approval-list-item';
import { AssignmentService } from '@web/services/assignment.service';
import { CurrentUserService } from '@core/services/current-user.service';
import { ReportApprovalStatus } from '@core/enums/report-approval-status';
import { SetMyReportApprovalLists } from '@core/store/actions/my-items.action';
import { OscPlatform } from '@osc-utils/enums/osc-platform';
import { UserProfile } from '@osc-utils/models/user-profile';
import { Store } from '@ngrx/store';
import { Observable, catchError, combineLatest, firstValueFrom, map, of, tap } from 'rxjs';
import { platform } from 'src/platforms/platform';
import { LoggerService } from '@shared/services/logger.service';
import { ApprovalsService } from '@shared/services/approvals.service';
import { AppState } from '../../app.state';

@Injectable({
  providedIn: 'root',
})
export class ReportRoleService {
  #assignmentService = inject(AssignmentService);
  #currentUserService = inject(CurrentUserService);
  #approvalsService = inject(ApprovalsService);
  #store = inject<Store<AppState>>(Store);
  #logger = inject(LoggerService);

  public async isInspector(projectId: string, jobId: string, taskId: string): Promise<boolean> {
    const userProfile = await this.getCurrentUser();
    const assignments = await this.#assignmentService.getTasksAssignments(projectId, jobId, taskId);
    return (
      assignments[0] &&
      assignments[0].assignedUserId === userProfile.id &&
      (platform.name === OscPlatform.Android ||
        platform.name === OscPlatform.IOs ||
        platform.name === OscPlatform.Window ||
        platform.name === OscPlatform.Web)
    );
  }

  public isApprover(taskId: string): Observable<boolean> {
    return this.getCurrentUserApproval(taskId).pipe(map(approval => !!approval));
  }

  public getCurrentUserApproval(taskId: string): Observable<MyReportApprovalListItem> {
    return combineLatest([this.#approvalsService.fetchTaskApprovals(taskId), this.#currentUserService.currentUser$]).pipe(
      map(([approvals, currentUser]) => {
        for (const approval of approvals) {
          if (approval.approver?.userId === currentUser.id) {
            return {
              taskId: approval.taskId,
              status: approval.approver.status || ReportApprovalStatus.Pending,
              signedDateUtc: approval.approver.status === ReportApprovalStatus.Signed ? approval.approver.dateApprovedUtc : null,
              signatureBase64: approval.approver.signatureBase64,
              taskName: approval.taskName,
              taskStatus: '',
              jobId: approval.jobId,
              jobName: approval.jobName,
              projectId: approval.hubId,
              projectName: approval.hubName,
              inspectionReportApprovalId: approval.id,
            } as MyReportApprovalListItem;
          }
        }
        return null;
      }),
      tap(item => this.#store.dispatch(SetMyReportApprovalLists({ items: item ? [item] : [] }))),
      catchError(err => {
        this.#logger.error('getCurrentUserApproval Failed: ', err);
        return of(null);
      }),
    );
  }

  public async isProjectManager(assignments: Assignment[]): Promise<boolean> {
    const userProfile = await this.getCurrentUser();
    const isCurrentUserManager = assignments.filter(assignment => userProfile.id === assignment.assignedUserId && assignment.isManager);
    return isCurrentUserManager.length > 0;
  }

  private async getCurrentUser(): Promise<UserProfile> {
    return await firstValueFrom(this.#currentUserService.currentUser$);
  }
}
